import {
  BarChart3,
  Check,
  Copy,
  Edit3,
  Film,
  Link,
  Loader2,
  RefreshCw,
  Shuffle,
  ThumbsUp,
  Trash2,
  Wand2,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAction, useMutation } from "convex/react";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GROW_EVENTS } from "@/constants/analyticsEvents";
import { HookAnalytics } from "./HookAnalytics";
import { HookScriptGeneration } from "./HookScriptGeneration";
import { Id } from "~/convex/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { VideoUrlDialog } from "./VideoUrlDialog";
import { api } from "~/convex/_generated/api";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useToast } from "@/components/ui/use-toast";

interface Hook {
  id: string;
  text: string;
  category: string;
  reasoning: string;
  potentialPaths?: string[];
  isLiked?: boolean;
}

interface GeneratedHooksData {
  hooks: Hook[];
  currentVariationType: "variations" | "same_topic" | "same_structure" | null;
  guidance: string;
}

interface HookDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hook: Hook | null;
  creatorProfileId: Id<"exp__creatorProfile"> | null;
  onLikeHook: (hook: Hook) => void;
  onCopyHook: (text: string) => void;
  likedHooks: Set<string>;
  generatedHooksData: GeneratedHooksData;
  onGeneratedHooksUpdate: (
    updater: (current: GeneratedHooksData) => GeneratedHooksData,
  ) => void;
  onClearGeneratedHooks: () => void;
  onUpdateHook?: (originalText: string, newText: string) => void;
  // Stage-based props
  stage?: "generate" | "liked" | "filmed" | "posted";
  likedHookData?: {
    _id: string;
    hookText: string;
    hookCategory: string;
    hookReasoning: string;
    originalDirection?: string;
    likedAt: number;
    isFilmed?: boolean;
    isPosted?: boolean;
    scriptContent?: string;
    videoUrl?: string;
  } | null;
  onMarkAsFilmed?: (hookText: string) => void;
  onMarkAsPosted?: (hookText: string) => void;
}

export const HookDetailDialog = ({
  open,
  onOpenChange,
  hook,
  creatorProfileId,
  onLikeHook,
  onCopyHook,
  likedHooks,
  generatedHooksData,
  onGeneratedHooksUpdate,
  onClearGeneratedHooks,
  onUpdateHook,
  stage = "generate",
  likedHookData,
  onMarkAsPosted,
}: HookDetailDialogProps) => {
  const { toast } = useToast();
  const { track } = useMixpanel();
  const [isGenerating, setIsGenerating] = useState(false);
  const [autoReplenishEnabled, setAutoReplenishEnabled] = useState(true);
  const [editingHookId, setEditingHookId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [localGuidance, setLocalGuidance] = useState("");

  // Stage-based state
  const [currentView, setCurrentView] = useState<
    "main" | "script" | "analytics"
  >("main");
  const [isVideoUrlDialogOpen, setIsVideoUrlDialogOpen] = useState(false);
  const [isAddingVideo, setIsAddingVideo] = useState(false);

  const addVideoUrlToHook = useMutation(
    api.exp__creatorInsights.exp__addVideoUrlToHook,
  );

  // Use the external state
  const {
    hooks: generatedHooks,
    currentVariationType,
    guidance,
  } = generatedHooksData;

  const generateHookVariations = useAction(
    api.generate.exp__generateHookVariations,
  );
  const updateLikedHookText = useMutation(
    api.exp__creatorInsights.exp__updateLikedHookText,
  );

  const handleGenerateVariations = async (
    variationType: "variations" | "same_topic" | "same_structure",
    isAutoReplenish = false,
  ) => {
    if (!hook || !creatorProfileId) return;

    // Track hook variation generation start
    track(GROW_EVENTS.HOOK_VARIATIONS_GENERATED, {
      creator_profile_id: creatorProfileId,
      original_hook_text: hook.text,
      original_hook_category: hook.category,
      variation_type: variationType,
      has_custom_guidance: !!localGuidance.trim(),
      guidance_length: localGuidance.trim().length,
      is_auto_replenish: isAutoReplenish,
      existing_variations_count: generatedHooks.length,
      stage: stage,
    });

    setIsGenerating(true);

    try {
      const result = await generateHookVariations({
        creatorId: creatorProfileId,
        originalHook: {
          text: hook.text,
          category: hook.category,
          reasoning: hook.reasoning,
          potentialPaths: hook.potentialPaths,
        },
        variationType,
        guidance: localGuidance || undefined,
      });

      const hooksWithIds = result.hooks.map(
        (
          generatedHook: {
            text: string;
            category: string;
            reasoning: string;
            potentialPaths?: string[];
          },
          index: number,
        ) => ({
          ...generatedHook,
          id: `variation_${Date.now()}_${index}`,
        }),
      );

      // Update the external state
      onGeneratedHooksUpdate((current) => ({
        ...current,
        hooks: isAutoReplenish
          ? [...current.hooks, ...hooksWithIds]
          : hooksWithIds,
        currentVariationType: isAutoReplenish
          ? current.currentVariationType
          : variationType,
        guidance: isAutoReplenish ? current.guidance : localGuidance,
      }));

      const variationTypeLabels = {
        variations: "variations",
        same_topic: "topic variations",
        same_structure: "structure variations",
      };

      if (!isAutoReplenish) {
        toast({
          title: "Variations Generated!",
          description: `Generated 5 ${variationTypeLabels[variationType]} for your hook.`,
        });
      }
    } catch (error) {
      console.error("Error generating hook variations:", error);
      if (!isAutoReplenish) {
        toast({
          title: "Generation Failed",
          description: "Failed to generate variations. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRemoveVariation = (hookId: string) => {
    const hookToRemove = generatedHooks.find((h) => h.id === hookId);

    // Track hook removal
    if (hookToRemove) {
      track(GROW_EVENTS.HOOK_REMOVED, {
        action: "remove_variation",
        hook_text: hookToRemove.text,
        hook_category: hookToRemove.category,
        hook_id: hookId,
        creator_profile_id: creatorProfileId,
        timestamp: new Date().toISOString(),
        source_stage: stage,
        interaction_type: "dialog_button",
        dialog_stage: stage,
        current_view: currentView,
        variation_type: currentVariationType,
        is_variation: true,
        remaining_variations: generatedHooks.length - 1,
      });
    }

    onGeneratedHooksUpdate((current) => ({
      ...current,
      hooks: current.hooks.filter((h) => h.id !== hookId),
    }));
  };

  const handleStartEdit = (hookId: string, currentText: string) => {
    setEditingHookId(hookId);
    setEditingText(currentText);
  };

  const handleCancelEdit = () => {
    setEditingHookId(null);
    setEditingText("");
  };

  const handleSaveEdit = async (hookId: string, originalText: string) => {
    if (!editingText.trim() || !creatorProfileId) return;

    try {
      // Update the hook in the local state
      if (hookId === "original" && hook) {
        // If this hook is liked, update it in the backend
        if (likedHooks.has(originalText)) {
          await updateLikedHookText({
            creatorId: creatorProfileId,
            originalHookText: originalText,
            newHookText: editingText.trim(),
          });
        }

        // Update the hook through the parent component if callback is provided
        if (onUpdateHook) {
          onUpdateHook(originalText, editingText.trim());
        }
      } else {
        // Update generated hook
        onGeneratedHooksUpdate((current) => ({
          ...current,
          hooks: current.hooks.map((h) =>
            h.id === hookId ? { ...h, text: editingText.trim() } : h,
          ),
        }));

        // If this generated hook is liked, update it in the backend
        if (likedHooks.has(originalText)) {
          await updateLikedHookText({
            creatorId: creatorProfileId,
            originalHookText: originalText,
            newHookText: editingText.trim(),
          });
        }
      }

      toast({
        title: "Hook Updated",
        description: "Hook text has been updated successfully.",
      });

      setEditingHookId(null);
      setEditingText("");
    } catch (error) {
      console.error("Error updating hook:", error);
      toast({
        title: "Update Failed",
        description: "Failed to update hook text. Please try again.",
        variant: "destructive",
      });
    }
  };

  const resetDialog = () => {
    setIsGenerating(false);
    setAutoReplenishEnabled(true);
    setEditingHookId(null);
    setEditingText("");
    setCurrentView("main");
    setIsVideoUrlDialogOpen(false);
    setIsAddingVideo(false);
    // Note: We no longer reset the generated hooks here - they persist across sessions
  };

  // Sync local guidance with external state when dialog opens
  useEffect(() => {
    if (open && guidance) {
      setLocalGuidance(guidance);
    } else if (open) {
      setLocalGuidance("");
    }
  }, [open, guidance]);

  // Auto-generate variations when dialog opens with a hook (only if no hooks exist and in generate stage)
  useEffect(() => {
    if (
      open &&
      hook &&
      creatorProfileId &&
      generatedHooks.length === 0 &&
      !isGenerating &&
      stage === "generate" // Only auto-generate in generate stage
    ) {
      // Start with "variations" type as default
      handleGenerateVariations("variations");
    }
  }, [open, hook, creatorProfileId, stage]);

  // Auto-open analytics for posted hooks, script for liked hooks
  useEffect(() => {
    if (open && stage === "posted") {
      setCurrentView("analytics");
    } else if (open && stage === "liked") {
      setCurrentView("script");
    } else if (open && stage !== "posted" && stage !== "liked") {
      setCurrentView("main");
    }
  }, [open, stage]);

  // Auto-replenish hooks when count goes below 7 (only in generate stage)
  useEffect(() => {
    if (
      autoReplenishEnabled &&
      generatedHooks.length > 0 &&
      generatedHooks.length < 7 &&
      !isGenerating &&
      currentVariationType &&
      hook &&
      creatorProfileId &&
      stage === "generate" // Only auto-replenish in generate stage
    ) {
      // Trigger auto-replenishment with the same variation type as the last generation
      handleGenerateVariations(currentVariationType, true);
    }
  }, [
    generatedHooks.length,
    autoReplenishEnabled,
    isGenerating,
    currentVariationType,
    hook,
    creatorProfileId,
    stage,
  ]);

  // Handle adding video URL to posted hook
  const handleAddVideoUrl = async (videoUrl: string) => {
    if (!creatorProfileId || !likedHookData) return;

    setIsAddingVideo(true);
    try {
      await addVideoUrlToHook({
        creatorId: creatorProfileId,
        hookText: likedHookData.hookText,
        videoUrl: videoUrl,
      });

      // Always call onMarkAsPosted to move the hook to posted column
      if (onMarkAsPosted) {
        onMarkAsPosted(likedHookData.hookText);
      }

      toast({
        title: "Video Added!",
        description:
          "Hook has been marked as posted with video URL and moved to the Posted column.",
      });

      setIsVideoUrlDialogOpen(false);
      onOpenChange(false); // Close the main dialog after successful submission
    } catch (error) {
      console.error("Error adding video URL:", error);
      toast({
        title: "Failed to Add Video",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAddingVideo(false);
    }
  };

  // Render different content based on stage and current view
  const renderContent = () => {
    if (
      stage === "liked" &&
      currentView === "script" &&
      likedHookData &&
      creatorProfileId
    ) {
      return (
        <HookScriptGeneration
          hook={likedHookData}
          creatorProfileId={creatorProfileId}
        />
      );
    }

    if (
      stage === "posted" &&
      currentView === "analytics" &&
      likedHookData &&
      creatorProfileId
    ) {
      return (
        <HookAnalytics
          hook={likedHookData}
          creatorProfileId={creatorProfileId}
        />
      );
    }

    // Main dialog content (variations for generated hooks, or main view for liked/filmed/posted)
    return renderMainContent();
  };

  const renderMainContent = () => {
    if (stage === "liked" && likedHookData) {
      return renderLikedHookContent();
    }

    if (stage === "filmed" && likedHookData) {
      return renderFilmedHookContent();
    }

    if (stage === "posted" && likedHookData) {
      return renderPostedHookContent();
    }

    // Default: render generate stage content
    return renderGenerateStageContent();
  };

  const renderLikedHookContent = () => (
    <div className="overflow-y-auto max-h-[calc(90vh-10rem)] px-6 pb-6 scrollbar-hide">
      <div className="space-y-4">
        {/* Liked Hook Display */}
        <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                Liked Hook
              </span>
              <Badge variant="secondary" className="text-xs">
                {likedHookData?.hookCategory}
              </Badge>
            </div>
          </div>
          <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">
            {likedHookData?.hookText}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {likedHookData?.hookReasoning}
          </p>
        </div>

        {/* Script Generation Actions */}
        <div className="space-y-3">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2">Ready to Script?</h3>
            <p className="text-muted-foreground mb-4">
              Generate a complete script based on this hook, or view your
              existing script.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <Button
              onClick={() => {
                // Track view change
                track(GROW_EVENTS.DIALOG_VIEW_CHANGED, {
                  from_view: currentView,
                  to_view: "script",
                  stage: stage,
                  creator_profile_id: creatorProfileId,
                  timestamp: new Date().toISOString(),
                  hook_text: likedHookData?.hookText,
                  has_existing_script: !!likedHookData?.scriptContent,
                });
                setCurrentView("script");
              }}
              className="w-full h-auto p-4 flex flex-col items-center gap-2"
            >
              <Edit3 className="w-6 h-6" />
              <div className="text-center">
                <div className="font-medium">
                  {likedHookData?.scriptContent
                    ? "Edit Script"
                    : "Generate Script"}
                </div>
                <div className="text-xs opacity-80">
                  {likedHookData?.scriptContent
                    ? "View and edit your existing script"
                    : "Create a complete video script from this hook"}
                </div>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFilmedHookContent = () => (
    <div className="overflow-y-auto max-h-[calc(90vh-10rem)] px-6 pb-6 scrollbar-hide">
      <div className="space-y-4">
        {/* Filmed Hook Display */}
        <div className="border rounded-lg p-4 bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                Filmed Hook
              </span>
              <Badge variant="secondary" className="text-xs">
                {likedHookData?.hookCategory}
              </Badge>
            </div>
          </div>
          <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">
            {likedHookData?.hookText}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {likedHookData?.hookReasoning}
          </p>
        </div>

        {/* Post Actions */}
        <div className="space-y-3">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2">Ready to Post?</h3>
            <p className="text-muted-foreground mb-4">
              Add your video URL to move this to the Posted column and track
              analytics.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <Button
              onClick={() => setIsVideoUrlDialogOpen(true)}
              className="w-full h-auto p-4 flex flex-col items-center gap-2"
            >
              <Link className="w-6 h-6" />
              <div className="text-center">
                <div className="font-medium">Add Video URL</div>
                <div className="text-xs opacity-80">
                  Mark as posted and enable analytics tracking
                </div>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPostedHookContent = () => (
    <div className="overflow-y-auto max-h-[calc(90vh-10rem)] px-6 pb-6 scrollbar-hide">
      <div className="space-y-4">
        {/* Posted Hook Display */}
        <div className="border rounded-lg p-4 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                Posted Hook
              </span>
              <Badge variant="secondary" className="text-xs">
                {likedHookData?.hookCategory}
              </Badge>
            </div>
          </div>
          <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">
            {likedHookData?.hookText}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {likedHookData?.hookReasoning}
          </p>
          {likedHookData?.videoUrl && (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-green-600 dark:text-green-400">
                Video URL:
              </span>
              <a
                href={likedHookData.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                {likedHookData.videoUrl.length > 50
                  ? `${likedHookData.videoUrl.substring(0, 50)}...`
                  : likedHookData.videoUrl}
              </a>
            </div>
          )}
        </div>

        {/* Analytics Actions */}
        <div className="space-y-3">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2">
              Performance Analytics
            </h3>
            <p className="text-muted-foreground mb-4">
              Track how well your video is performing across platforms.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <Button
              onClick={() => {
                // Track view change to analytics
                track(GROW_EVENTS.DIALOG_VIEW_CHANGED, {
                  from_view: currentView,
                  to_view: "analytics",
                  stage: stage,
                  creator_profile_id: creatorProfileId,
                  timestamp: new Date().toISOString(),
                  hook_text: likedHookData?.hookText,
                  has_video_url: !!likedHookData?.videoUrl,
                });
                setCurrentView("analytics");
              }}
              className="w-full h-auto p-4 flex flex-col items-center gap-2"
            >
              <BarChart3 className="w-6 h-6" />
              <div className="text-center">
                <div className="font-medium">View Analytics</div>
                <div className="text-xs opacity-80">
                  See views, likes, comments, and engagement data
                </div>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGenerateStageContent = () => {
    if (!hook) return null;

    return (
      <div className="overflow-y-auto max-h-[calc(90vh-10rem)] px-6 pb-6 scrollbar-hide">
        <div className="space-y-4">
          {/* Original Hook */}
          <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  Original Hook
                </span>
                <Badge variant="secondary" className="text-xs">
                  {hook.category}
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleStartEdit("original", hook.text)}
                disabled={editingHookId === "original"}
                className="h-8 w-8 p-0"
              >
                <Edit3 className="w-3 h-3" />
              </Button>
            </div>

            {editingHookId === "original" ? (
              <div className="space-y-2">
                <Input
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  className="font-medium"
                  placeholder="Edit hook text..."
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleSaveEdit("original", hook.text)}
                    disabled={!editingText.trim()}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelEdit}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {hook.text}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {hook.reasoning}
                </p>
              </>
            )}
          </div>

          {/* Guidance Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Custom Guidance (Optional)
            </label>
            <Textarea
              placeholder="e.g., 'Make them more casual' or 'Focus on actionable tips' or 'Add more urgency'"
              value={localGuidance}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setLocalGuidance(e.target.value)
              }
              className="resize-none"
              rows={2}
            />
          </div>

          {/* Clear and Generation Buttons */}
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Button
                onClick={() => handleGenerateVariations("variations")}
                disabled={isGenerating}
                variant="outline"
                className="w-full h-auto p-4 flex flex-col items-center gap-2"
              >
                <RefreshCw
                  className={`w-5 h-5 ${
                    isGenerating && currentVariationType === "variations"
                      ? "animate-spin"
                      : ""
                  }`}
                />
                <div className="text-center">
                  <div className="font-medium">More Variations</div>
                  <div className="text-xs text-gray-500">
                    Same message, different wording
                  </div>
                </div>
              </Button>

              <Button
                onClick={() => handleGenerateVariations("same_topic")}
                disabled={isGenerating}
                variant="outline"
                className="w-full h-auto p-4 flex flex-col items-center gap-2"
              >
                <Shuffle
                  className={`w-5 h-5 ${
                    isGenerating && currentVariationType === "same_topic"
                      ? "animate-spin"
                      : ""
                  }`}
                />
                <div className="text-center">
                  <div className="font-medium">Different Structures</div>
                  <div className="text-xs text-gray-500">
                    Same topic, different approaches
                  </div>
                </div>
              </Button>

              <Button
                onClick={() => handleGenerateVariations("same_structure")}
                disabled={isGenerating}
                variant="outline"
                className="w-full h-auto p-4 flex flex-col items-center gap-2"
              >
                <Wand2
                  className={`w-5 h-5 ${
                    isGenerating && currentVariationType === "same_structure"
                      ? "animate-spin"
                      : ""
                  }`}
                />
                <div className="text-center">
                  <div className="font-medium">Same Pattern</div>
                  <div className="text-xs text-gray-500">
                    Same structure, different topics
                  </div>
                </div>
              </Button>
            </div>
          </div>
          {generatedHooks.length > 0 && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onClearGeneratedHooks();
                  setLocalGuidance("");
                }}
                className="text-red-600 hover:text-red-700 hover:border-red-300"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Clear All Variations
              </Button>
            </div>
          )}

          {/* Generated Hooks */}
          {(generatedHooks.length > 0 || isGenerating) && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">
                  {isGenerating ? "Generating..." : "Generated Variations"}
                </h3>
                <div className="flex items-center gap-3">
                  {generatedHooks.length > 0 && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {generatedHooks.length} hooks
                    </span>
                  )}
                  {generatedHooks.length > 0 && (
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-600 dark:text-gray-400">
                        Auto-refill
                      </label>
                      <input
                        type="checkbox"
                        checked={autoReplenishEnabled}
                        onChange={(e) =>
                          setAutoReplenishEnabled(e.target.checked)
                        }
                        className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {isGenerating && generatedHooks.length === 0 && (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-blue-500" />
                    <p className="text-lg font-semibold">
                      Generating Variations
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Creating 5 new hooks tailored to your style...
                    </p>
                  </div>
                )}

                {isGenerating && generatedHooks.length > 0 && (
                  <div className="text-center py-4 border rounded-lg bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                        Auto-generating 5 more variations...
                      </p>
                    </div>
                  </div>
                )}

                {generatedHooks.map((generatedHook, index) => (
                  <div
                    key={generatedHook.id}
                    className={`border rounded-lg p-4 ${
                      likedHooks.has(generatedHook.text)
                        ? "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20"
                        : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                            #{index + 1}
                          </span>
                          <Badge variant="secondary" className="text-xs">
                            {generatedHook.category}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleStartEdit(
                                generatedHook.id,
                                generatedHook.text,
                              )
                            }
                            disabled={editingHookId === generatedHook.id}
                            className="h-6 w-6 p-0 ml-auto"
                          >
                            <Edit3 className="w-3 h-3" />
                          </Button>
                        </div>

                        {editingHookId === generatedHook.id ? (
                          <div className="space-y-2">
                            <Input
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                              className="font-medium"
                              placeholder="Edit hook text..."
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() =>
                                  handleSaveEdit(
                                    generatedHook.id,
                                    generatedHook.text,
                                  )
                                }
                                disabled={!editingText.trim()}
                              >
                                <Check className="w-3 h-3 mr-1" />
                                Save
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleCancelEdit}
                              >
                                <X className="w-3 h-3 mr-1" />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                              {generatedHook.text}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {generatedHook.reasoning}
                            </p>
                          </>
                        )}
                      </div>

                      {editingHookId !== generatedHook.id && (
                        <div className="flex gap-2">
                          <Button
                            variant={
                              likedHooks.has(generatedHook.text)
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() => onLikeHook(generatedHook)}
                            className={`${
                              likedHooks.has(generatedHook.text)
                                ? "bg-blue-600 hover:bg-blue-700 text-white"
                                : "hover:border-blue-400 hover:text-blue-600"
                            }`}
                          >
                            <ThumbsUp
                              className={`w-3 h-3 mr-1 ${
                                likedHooks.has(generatedHook.text)
                                  ? "fill-current"
                                  : ""
                              }`}
                            />
                            {likedHooks.has(generatedHook.text)
                              ? "Liked"
                              : "Like"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Track copy action from dialog
                              track(GROW_EVENTS.HOOK_COPIED, {
                                action: "copy_from_dialog",
                                hook_text: generatedHook.text,
                                hook_category: generatedHook.category,
                                hook_id: generatedHook.id,
                                creator_profile_id: creatorProfileId,
                                timestamp: new Date().toISOString(),
                                source_stage: stage,
                                interaction_type: "dialog_button",
                                dialog_stage: stage,
                                current_view: currentView,
                                variation_type: currentVariationType,
                                is_variation: true,
                              });
                              onCopyHook(generatedHook.text);
                            }}
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Copy
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              handleRemoveVariation(generatedHook.id)
                            }
                            className="text-red-600 hover:text-red-700 hover:border-red-300"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        onOpenChange(newOpen);
        if (!newOpen) {
          // Reset state when dialog closes
          setTimeout(resetDialog, 150); // Small delay to allow close animation
        }
      }}
    >
      <DialogContent
        className={`max-w-4xl max-h-[90vh] p-0 ${
          (stage === "liked" && currentView === "script") ||
          (stage === "posted" && currentView === "analytics")
            ? "overflow-auto"
            : "overflow-hidden"
        }`}
      >
        {currentView === "main" && (
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {stage === "liked" ? (
                  <>
                    <Edit3 className="w-5 h-5" />
                    Script Generation
                  </>
                ) : stage === "filmed" ? (
                  <>
                    <Film className="w-5 h-5" />
                    Add Video URL
                  </>
                ) : stage === "posted" ? (
                  <>
                    <BarChart3 className="w-5 h-5" />
                    Video Analytics
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    Hook Variations & Alternatives
                  </>
                )}
              </DialogTitle>
              <DialogDescription>
                {stage === "liked"
                  ? "Generate a complete script based on this hook and prepare for filming."
                  : stage === "filmed"
                    ? "Add your video URL to move this hook to the Posted column and enable analytics tracking."
                    : stage === "posted"
                      ? "View analytics and performance metrics for your posted content."
                      : "Generate different variations, explore the same topic with different structures, or apply the same pattern to other topics."}
              </DialogDescription>
            </DialogHeader>
          </div>
        )}

        {renderContent()}
      </DialogContent>

      {/* Video URL Dialog */}
      <VideoUrlDialog
        open={isVideoUrlDialogOpen}
        onOpenChange={setIsVideoUrlDialogOpen}
        onSubmit={handleAddVideoUrl}
        isSubmitting={isAddingVideo}
      />
    </Dialog>
  );
};
